import moment from "moment";
import * as linkify from 'linkifyjs';
import config from "@/config/constants.js";
import {isVideo, isImage} from "@/util/video_utils_v2";
import LinkPrevue from 'link-prevue';
import UserAvatar from '@/components/UserAvatar';
import ReactionsListModal from '@/views/chat/modals/ReactionsListModal';

export default {
  name: "Message",
  props: {
    index: {
      type: Number,
      default: 0
    },
    item: {
      type: Object,
      default: () => {
      }
    },
    messagePosition: {
      type: Object,
      default: () => {
      }
    },
    owner_id: {
      type: String,
      default: ""
    },
    tenant_id: {
      type: String,
      default: ""
    },
    showDate: {
      type: Function,
      default: () => {
      }
    },
    selectedChat: {
      type: Object,
      default: () => {
      }
    },
    infoRoute: {
      type: Object,
      default: () => {
      }
    },
    selectedChatId: {
      type: String,
      default: ""
    },
    status_contact: {
      type: String,
      default: ""
    },
    target: {
      type: String,
      default: ""
    },
    mediaFilesChat: {
      type: Array,
      default: []
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    isLastOwnMessage: {
      type: Boolean,
      default: false
    },
    getImageURL: {
      type: Function,
      default: () => {
      }
    },
    getMediaPreviewURL: {
      type: Function,
      default: () => {
      }
    },
    showImagePreview: {
      type: Function,
      default: () => {
      }
    },
    showVideoPreview: {
      type: Function,
      default: () => {
      }
    },
    message_contact: {
      type: Function,
      default: () => {
      }
    },
    add_contact: {
      type: Function,
      default: () => {
      }
    },
    generate_route: {
      type: Function,
      default: () => {
      }
    },
    deleteMessage: {
      type: Function,
      default: () => {
      }
    },
    editMessage: {
      type: Function,
      default: () => {
      }
    },
    replyMessage: {
      type: Function,
      default: () => {
      }
    },
    forwardMessage: {
      type: Function,
      default: () => {
      }
    },
    resendMessage: {
      type: Function,
      default: () => {
      }
    },
    create_image_from_pdf: {
      type: Function,
      default: () => {
      }
    },
    onReaction: {
      type: Function,
      default: () => {
      }
    },
    eventAction: {
      type: Function,
      default: () => {
      }
    },
    searchText: {
      type: String,
      default: ""
    },
    searchResults: {
      type: Array,
      default: () => []
    },
    currentResultIndex: {
      type: Number,
      default: 0
    }
  },
  components: {
    LinkPrevue,
    UserAvatar,
    ReactionsListModal
  },
  computed: {
    // Display name for the sender label above the bubble. The Kiey AI
    // synthetic participant ("kiey-ai-assistant") has no first/last name on
    // its user doc — rendering item.first_name + ' ' + item.last_name yields
    // "undefined undefined". Detect the AI sender and substitute "Kiey AI".
    senderDisplayName() {
      if (this.item && this.item.owner_id === 'kiey-ai-assistant') return 'Kiey AI';
      const f = (this.item && this.item.first_name) || '';
      const l = (this.item && this.item.last_name) || '';
      return (f + ' ' + l).trim();
    },
    getType() {
      if (this.item.type === "media") {
        if (this.item.is_from_web) {
          return '';
        }
        var resource = this.checkResource(this.item.media, this.mediaFilesChat);
        if (resource === 'image') return '#photo';
        if (resource === 'video') return '#media';
        if (resource === 'document') return '#document';
        return '#media';
      }
      return '#' + this.item.type;
    },
    // Kept for backward compatibility even though it's no longer consumed.
    // Read indicators now derive from chat.unread_counters (which every
    // platform maintains) instead of message.read_statuses (which only
    // Web writes). Leaving this computed in place avoids a wider refactor
    // if anything downstream still references it.
    _otherReadTimestamps() {
      if (!this.item || this.item.owner_id !== this.owner_id) return [];
      const statuses = this.item.read_statuses;
      if (!statuses || Object.keys(statuses).length === 0) return [];
      const toDate = (ts) => {
        if (!ts) return null;
        if (ts === true || ts === false) return null;
        if (typeof ts.toDate === 'function') return ts.toDate();
        if (ts._seconds) return new Date(ts._seconds * 1000);
        if (ts instanceof Date) return ts;
        if (typeof ts === 'number') return new Date(ts < 1e12 ? ts * 1000 : ts);
        const d = new Date(ts);
        return isNaN(d.getTime()) ? null : d;
      };
      return Object.entries(statuses)
        .filter(([uid]) => uid !== this.owner_id)
        .map(([uid, ts]) => [uid, toDate(ts)])
        .filter(([, d]) => d !== null);
    },
    // Returns the list of "other participant" Auth UIDs who have read this
    // message, derived from chat.unread_counters. Algorithm (ported from
    // iOS ConversationContentViewModel):
    //   - chat.unread_counters[uid] is an index into the messages list in
    //     NEWEST-FIRST order. A message at newest-first index i has been
    //     read by uid iff unread_counters[uid] <= i.
    //   - The Vuex store (state.Chat.messages) is OLDEST-FIRST (last
    //     element is newest), so we convert: i = (N - 1) - j where j is
    //     the oldest-first index.
    // This is the shared core used by both isActuallyRead and
    // readReceiptText to avoid duplicated work.
    _readersFromUnreadCounters() {
      if (!this.item || this.item.owner_id !== this.owner_id) return [];
      const chat = this.selectedChat;
      if (!chat || !chat.unread_counters) return [];

      // Full, unfiltered backing array from the Vuex store. Backend
      // increments unread_counters for every message it writes, not just
      // the ones this UI renders, so we must index against the raw list.
      const messages = (this.$store && this.$store.state && this.$store.state.Chat && this.$store.state.Chat.messages) || [];
      if (!messages.length) return [];

      const myId = this.item.message_id;
      const oldestIndex = messages.findIndex(m => m && m.message_id === myId);
      if (oldestIndex === -1) return [];

      // Convert oldest-first position → newest-first index.
      const newestIndex = (messages.length - 1) - oldestIndex;

      const participants = Array.isArray(chat.participants) ? chat.participants : [];
      const readers = [];
      for (const p of participants) {
        const uid = p && p.user_id;
        if (!uid) continue;
        if (uid === this.owner_id) continue;
        // Exclude the synthetic AI assistant — otherwise every outgoing
        // message in an AI chat would appear permanently unread.
        if (uid === 'kiey-ai-assistant') continue;

        const count = chat.unread_counters[uid];
        if (typeof count !== 'number') continue;
        if (count <= newestIndex) readers.push(uid);
      }
      return readers;
    },
    isActuallyRead() {
      if (!this.item || this.item.owner_id !== this.owner_id) return false;
      return this._readersFromUnreadCounters.length > 0;
    },
    readReceiptText() {
      if (!this.item || this.item.owner_id !== this.owner_id) return '';
      const readers = this._readersFromUnreadCounters;
      if (readers.length === 0) return '';
      if (this.selectedChat && this.selectedChat.is_group) {
        return 'Read by ' + readers.length;
      }
      return 'Read';
    },
    getReactions() {
      let reactions = [];
      if (Object.keys(this.item.reactions).length > 0) {
        for (const user_id in this.item.reactions) {
          reactions = reactions.concat(this.item.reactions[user_id]);
        }
      }

      return reactions;
    },
    getAllReactions() {
      return Object.keys(this.reaction_icons);
    },
    isEmojiOnly() {
      try {
        if (!this.item || !this.item.text || this.item.deleted) return false;
        if (this.item.type && this.item.type !== 'text') return false;
        const stripped = this.item.text.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').replace(/\n/g, '').trim();
        if (!stripped || stripped.length > 30) return false;
        // eslint-disable-next-line no-misleading-character-class
        const withoutEmoji = stripped.replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{200D}\u{20E3}\u{E0020}-\u{E007F}\u{FE0E}\u{FE0F}]/gu, '');
        return withoutEmoji.trim().length === 0;
      } catch (e) {
        return false;
      }
    },
    isAnsweredEvent() {
      return this.item.type === "event" ? this.item.event.answer_status[this.owner_id] : undefined;
    },
    isAcceptedEvent() {
      return this.item.type === "event" ? this.item.event.answer_status[this.owner_id] && this.item.event.answer_status[this.owner_id] === 'accepted' : "";
    },
    isSearchResult() {
      if (!this.searchText || !this.searchText.trim() || !this.searchResults) return false;
      return this.searchResults.some(msg => msg.message_id === this.item.message_id);
    },

    isCurrentSearchResult() {
      if (!this.isSearchResult || this.currentResultIndex === -1) return false;
      const currentMsg = this.searchResults[this.currentResultIndex];
      return currentMsg && currentMsg.message_id === this.item.message_id;
    }
  },
  data() {
    return {
      showReactionsModal: false,
      reaction_icons: {
        thumb_up: require('@/assets/img/reactions/thumb_up.png'),
        thumb_down: require('@/assets/img/reactions/thumb_down.png'),
        laugh: require('@/assets/img/reactions/laugh.png'),
        sad: require('@/assets/img/reactions/sad.png'),
        shocked: require('@/assets/img/reactions/shocked.png'),
        thinking: require('@/assets/img/reactions/thinking.png'),
        puzzled: require('@/assets/img/reactions/puzzled.png'),
        heart: require('@/assets/img/reactions/heart.png'),
        fire: require('@/assets/img/reactions/fire.png'),
        crossed_fingers: require('@/assets/img/reactions/crossed_fingers.png'),
        folded_hands: require('@/assets/img/reactions/folded_hands.png'),
        yes: require('@/assets/img/reactions/yes.png'),
      },
      apiUrl: config.api_url + "url_metadata",
      showMenu: false,
      // Voicemail playback state
      vmAudio: null,
      vmPlaying: false,
      vmProgress: 0,
      vmPlayingCallId: null,
    }
  },
  filters: {
    retrieve_url_filter(id, list) {
      let file = list.find(f => f.id === id);
      if (file) {
        return file['url'];
      }
    },
    link_address: function (address) {
      if (!address || !address.latitude || !address.longitude) return '#';
      return "https://www.google.com/maps?q=" + address.latitude + "," + address.longitude + "&z=17";
    },
    link_directions(ontheway, lat, long) {
      var origin = "&origin=" + lat + "," + long;
      var destination = "&destination=" + ontheway.latitude + "," + ontheway.longitude;
      var url = "https://www.google.com/maps/dir/?api=1" + origin + destination;
      return url;
    },
    formatTime: function (value) {
      if (!value) return '';
      var msgTime = moment(value);
      if (!msgTime.isValid()) return '';
      var now = moment();
      var diffMinutes = now.diff(msgTime, 'minutes');
      if (diffMinutes < 1) return 'Now';
      if (diffMinutes < 5) return diffMinutes + 'm';
      return msgTime.format('LT');
    },
    format_event_date: function (value) {
      if (value) {
        return moment(value).format('MM-DD-YY hh:mm A');
      }
      return '';
    },
    formatDate: function (value) {
      if (value) {
        var d = moment(new Date(value));
        return d.isValid() ? d.format('ddd, MMM DD') : '';
      }
      return '';
    },
  },
  beforeDestroy() {
    this._cleanupVmAudio();
  },
  methods: {
    // Voicemail playback methods
    isPlayingVoicemail(item) {
      const id = item.call_id || item.id;
      return this.vmPlaying && this.vmPlayingCallId === id;
    },
    voicemailProgress(item) {
      const id = item.call_id || item.id;
      if (this.vmPlayingCallId !== id) return 0;
      return this.vmProgress;
    },
    formatVoicemailDuration(seconds) {
      if (!seconds) return '0:00';
      const s = Math.round(seconds);
      const mins = Math.floor(s / 60);
      const secs = s % 60;
      return `${mins}:${String(secs).padStart(2, '0')}`;
    },
    _cleanupVmAudio() {
      if (this.vmAudio) {
        this.vmAudio.pause();
        this.vmAudio.onended = null;
        this.vmAudio.ontimeupdate = null;
        this.vmAudio = null;
      }
      this.vmPlaying = false;
      this.vmProgress = 0;
      this.vmPlayingCallId = null;
    },
    toggleVoicemailPlay(event, item) {
      event.stopPropagation();
      const id = item.call_id || item.id;

      // Pause current
      if (this.vmPlaying && this.vmPlayingCallId === id) {
        if (this.vmAudio) this.vmAudio.pause();
        this.vmPlaying = false;
        return;
      }

      // Resume paused (same item)
      if (!this.vmPlaying && this.vmPlayingCallId === id && this.vmAudio) {
        this.vmPlaying = true;
        this.vmAudio.play().catch(() => { this.vmPlaying = false; });
        return;
      }

      // New playback — stop any existing
      this._cleanupVmAudio();
      this.vmPlayingCallId = id;
      this.vmAudio = new Audio(item.voicemail_url);
      this.vmPlaying = true;
      this.vmProgress = 0;

      // Use property handlers (easy to clean up, no anonymous listener leaks)
      this.vmAudio.ontimeupdate = () => {
        if (this.vmAudio && this.vmAudio.duration) {
          this.vmProgress = (this.vmAudio.currentTime / this.vmAudio.duration) * 100;
        }
      };
      this.vmAudio.onended = () => {
        this.vmPlaying = false;
        this.vmProgress = 0;
      };
      this.vmAudio.play().catch(() => {
        this.vmPlaying = false;
      });
    },
    // Allowlist sanitizer — message text is rendered with v-html, so strip any
    // unsafe tags/attributes (scripts, on* handlers, javascript: hrefs) while
    // keeping the formatting we intentionally use (<br>, bold, links, mentions).
    sanitizeMessageHtml(html) {
      if (!html) return '';
      const ALLOWED = {
        BR: [], B: [], STRONG: [], I: [], EM: [], U: [], MARK: ['class'],
        A: ['href', 'target', 'rel'], SPAN: [], DIV: [], P: [],
        // markdown tables rendered by markdownTablesToHtml
        TABLE: ['class'], THEAD: [], TBODY: [], TR: [], TH: [], TD: []
      };
      const tmp = document.createElement('div');
      tmp.innerHTML = String(html);
      const walk = (node) => {
        Array.from(node.childNodes).forEach((child) => {
          if (child.nodeType === 1) {
            const tag = child.tagName;
            if (!ALLOWED[tag]) {
              node.replaceChild(document.createTextNode(child.textContent || ''), child);
              return;
            }
            Array.from(child.attributes).forEach((attr) => {
              const name = attr.name.toLowerCase();
              if (name.startsWith('on') || !ALLOWED[tag].includes(name)) child.removeAttribute(attr.name);
            });
            if (tag === 'A') {
              const href = child.getAttribute('href') || '';
              if (/^\s*(javascript|data|vbscript):/i.test(href)) child.removeAttribute('href');
              else if (href) { child.setAttribute('target', '_blank'); child.setAttribute('rel', 'noopener noreferrer'); }
            }
            walk(child);
          } else if (child.nodeType !== 3) {
            node.removeChild(child); // comments / others
          }
        });
      };
      walk(tmp);
      return tmp.innerHTML;
    },
    // Convert markdown table blocks (| a | b |\n|---|---|\n| c | d |) into real
    // HTML <table>s so pasted Claude/ChatGPT tables render like a table instead
    // of raw pipes + dash-lines. Cell text is HTML-escaped; the sanitizer
    // allowlist (sanitizeMessageHtml) permits the table tags this emits.
    markdownTablesToHtml(text) {
      // Rows may be separated by <br> (composer/stored format) rather than real
      // newlines — normalize both so table detection sees discrete lines.
      const lines = String(text).replace(/<br\s*\/?>/gi, '\n').split(/\r\n?|\n/);
      const esc = (s) => String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const splitCells = (row) => {
        let r = row.trim();
        if (r.startsWith('|')) r = r.slice(1);
        if (r.endsWith('|')) r = r.slice(0, -1);
        // honor escaped pipes (\|) inside cells via a private-use sentinel (no regex control chars)
        const SENT = '\uE000';
        return r.split('\\|').join(SENT).split('|').map((c) => c.split(SENT).join('|').trim());
      };
      const hasPipe = (l) => /\|/.test(l);
      const isSeparatorRow = (l) => {
        const t = l.trim();
        return !!t && /\|/.test(t) && /^\|?[\s:|-]+\|?$/.test(t) && /-/.test(t) && !/[a-z0-9]/i.test(t);
      };

      const out = [];
      for (let i = 0; i < lines.length; i++) {
        if (hasPipe(lines[i]) && i + 1 < lines.length && isSeparatorRow(lines[i + 1])) {
          const header = splitCells(lines[i]);
          const rows = [];
          let j = i + 2;
          while (j < lines.length && lines[j].trim() && hasPipe(lines[j])) {
            rows.push(splitCells(lines[j]));
            j++;
          }
          let html = '<table class="md-table"><thead><tr>';
          header.forEach((c) => { html += '<th>' + esc(c) + '</th>'; });
          html += '</tr></thead><tbody>';
          rows.forEach((cells) => {
            html += '<tr>';
            for (let k = 0; k < header.length; k++) html += '<td>' + esc(cells[k]) + '</td>';
            html += '</tr>';
          });
          html += '</tbody></table>';
          out.push(html);
          i = j - 1;
        } else {
          out.push(lines[i]);
        }
      }
      return out.join('\n');
    },
    // Wrap bare URLs/emails found in text nodes with clickable <a> tags, skipping
    // any text already inside an anchor. Runs on the sanitized DOM so it can't
    // double-link or touch existing markup.
    linkifyTextNodes(root) {
      const walk = (node) => {
        Array.from(node.childNodes).forEach((child) => {
          if (child.nodeType === 3) {
            const value = child.nodeValue || '';
            let matches;
            try {
              matches = linkify.find(value).filter((m) => m.type === 'url' || m.type === 'email');
            } catch (e) { matches = []; }
            if (!matches.length) return;
            const frag = document.createDocumentFragment();
            let last = 0;
            matches.forEach((m) => {
              const href = m.type === 'email' ? ('mailto:' + m.value) : m.href;
              // Only http(s)/mailto become links (same policy as verifyUrl). Anything
              // else (e.g. ftp://) is left as plain text rather than an anchor.
              if (!/^(https?:\/\/|mailto:)/i.test(href)) return;
              if (m.start > last) frag.appendChild(document.createTextNode(value.slice(last, m.start)));
              const a = document.createElement('a');
              a.setAttribute('href', href);
              a.setAttribute('target', '_blank');
              a.setAttribute('rel', 'noopener noreferrer');
              a.textContent = m.value;
              frag.appendChild(a);
              last = m.end;
            });
            if (last < value.length) frag.appendChild(document.createTextNode(value.slice(last)));
            node.replaceChild(frag, child);
          } else if (child.nodeType === 1 && child.tagName !== 'A') {
            walk(child);
          }
        });
      };
      walk(root);
    },
    // Full render pipeline for a text/link message body: markdown tables → HTML,
    // sanitize, then auto-link bare URLs. Used by highlightText when not searching.
    renderRichText(text) {
      if (!text) return '';
      let html = this.sanitizeMessageHtml(this.markdownTablesToHtml(text));
      // Only pay for the extra linkify DOM pass when the text could actually
      // hold a URL or email (the vast majority of messages have neither).
      if (/:\/\/|www\.|@/.test(String(text))) {
        try {
          const tmp = document.createElement('div');
          tmp.innerHTML = html;
          this.linkifyTextNodes(tmp);
          html = tmp.innerHTML;
        } catch (e) { /* fall back to sanitized html */ }
      }
      return html;
    },
    highlightText(text, searchTerm) {
      if (!searchTerm || !text) {
        return this.renderRichText(text);
      }

      // Convertir a string y limpiar
      let textStr = String(text);
      const searchStr = String(searchTerm).trim();

      if (!textStr || !searchStr) {
        return textStr;
      }

      // Limpiar HTML si existe
      const stripHtml = (html) => {
        if (!html) return '';
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
      };

      textStr = stripHtml(textStr);
      
      // Normalizar: eliminar espacios mltiples y caracteres especiales
      const normalize = (str) => {
        return str
          .replace(/\u200B/g, '')  // Zero-width space
          .replace(/\u200C/g, '')  // Zero-width non-joiner
          .replace(/\u200D/g, '')  // Zero-width joiner
          .replace(/\u200E/g, '')  // Left-to-right mark
          .replace(/\u200F/g, '')  // Right-to-left mark
          .replace(/\s+/g, ' ')    // Espacios mltiples a uno
          .trim();
      };

      textStr = normalize(textStr);
      const searchStrNorm = normalize(searchStr);

      // Escape HTML entities before highlighting to prevent XSS
      textStr = textStr.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

      // Escapar caracteres especiales para regex
      const escapedSearch = searchStrNorm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      // Buscar (case-insensitive)
      const regex = new RegExp(`(${escapedSearch})`, 'gi');
      return textStr.replace(regex, '<mark class="highlight">$1</mark>');
    },
    hideReactionsModal() {
      this.showReactionsModal = false;
    },
    isImage(file) {
      const fileTmp = {
        url: file.url
      };
      return isImage(fileTmp);
    },
    isVideo(file) {
      const fileTmp = {
        url: file.url
      };
      return isVideo(fileTmp);
    },
    checkResource(media, list) {
      // ?? Caso principal: viene media directamente
      if (media?.file_type) {

        // Videos
        if (
            media.file_subtype === 'video' ||
            media.file_subtype === 'movie'
        ) {
          return 'video';
        }

        // Imgenes
        if (
            media.file_subtype === 'photo' ||
            media.file_subtype === 'image'
        ) {
          return 'image';
        }

        // Cualquier otro media ? documento
        return 'document';
      }

      // ?? Fallback: buscar en list por file_id (legacy)
      if (media?.file_id && Array.isArray(list)) {
        const file = list.find(f => f.id === media.file_id);

        if (!file) return 'document';

        if (file.file_subtype === 'video') return 'video';
        if (file.file_subtype === 'photo' || file.file_subtype === 'image') return 'image';

        return 'document';
      }

      return false;
    },
    onLoadPreview() {
      setTimeout(() => {
        window.EventBus.$emit("onUpdateMessages")
      }, 100);
    },
    verifyUrl(url) {
      const u = String(url || '').trim();
      // Block dangerous schemes from becoming a link href.
      if (/^\s*(javascript|data|vbscript):/i.test(u)) return '#';
      if (!/^https?:\/\//i.test(u)) {
        return "http://" + u;
      }
      return u;
    },
    previewFile(item) {
      // Open the file in a new tab. Browsers natively preview PDFs, images,
      // text, JSON, XML, CSV, etc., and trigger a download for binary types
      // like .docx/.xlsx/.zip — which is the same behavior Slack and Gmail
      // attachments use. The previous path forced every document through
      // PDF.js and showed "Cannot preview this document" for anything that
      // wasn't a PDF.
      const url = this.getImageURL(item.media, this.mediaFilesChat);
      if (!url) return;
      // Force download for truly opaque types (e.g. .zip) so the user gets
      // the file immediately instead of a blank tab.
      const lowerUrl = url.toLowerCase();
      const isNativelyPreviewable = /\.(pdf|jpe?g|png|gif|webp|svg|bmp|txt|csv|json|xml|html?|md|log|js|ts|css)(\?|$)/.test(lowerUrl);
      if (isNativelyPreviewable) {
        window.open(url, '_blank', 'noopener');
      } else {
        this.downloadMedia(item);
      }
    },
    getMediaFilename(item) {
      if (!item || !item.media) return 'download';
      const m = item.media;
      if (typeof m === 'string') return m;
      return m.name || m.file_name || m.filename || m.original_name || m.file_id || 'download';
    },
    async downloadMedia(item) {
      const url = this.getImageURL(item.media, this.mediaFilesChat);
      if (!url) return;
      const filename = this.getMediaFilename(item);
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('fetch failed: ' + response.status);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      } catch (e) {
        window.open(url, '_blank', 'noopener');
      }
    }
  }
}